import {
  shuffleArray,
  useResponseValidityUpdate,
  useReactiveItemResponse,
  useResponseUpdate,
  useDebouncing
} from "../../helpers"
import { CurrentTestState } from "../../storage"
import {
  Collision,
  CollisionDetection,
  DragOverEvent,
  DragStartEvent,
  DragMoveEvent,
  KeyboardSensor,
  MouseSensor,
  pointerWithin,
  rectIntersection,
  useSensor,
  useSensors
} from "@dnd-kit/core"
import { arrayMove } from "@dnd-kit/sortable"
import {
  Dispatch,
  SetStateAction,
  useCallback,
  useEffect,
  useRef,
  useState
} from "react"
import { useSnapshot } from "valtio"
import {
  ContainersId,
  DragEvents,
  DraggableListChoices,
  MoveChoiceEnum,
  MoveChoiceType,
  UseContainersStateControlType,
  UseDragDropChoiceControlType
} from "./types"
import { getChoiceIndex, getListChoicesIds } from "./utils"
import { InteractionType, onSaveStudentAnswer } from "../../types"

const getInitialChoices = (
  response: string[],
  simpleChoices: JSX.Element[],
  shuffle: string
) => {
  const responseSet = new Set(response)
  return shuffleArray(shuffle === "true", simpleChoices)
    .filter(choice => !responseSet.has(choice.props.identifier))
    .map(choice => ({
      id: choice.props.identifier,
      element: choice
    }))
}

const getInitialSortableChoices = (
  response: string[],
  simpleChoices: JSX.Element[]
) => {
  const choiceMap = new Map(
    simpleChoices.map(choice => [choice.props.identifier, choice])
  )
  return response.map(id => ({
    id,
    element: choiceMap.get(id)
  }))
}

const getAllChoices = (simpleChoices: JSX.Element[]) =>
  simpleChoices.map(choice => ({
    id: choice.props.identifier,
    element: choice
  }))

const transformOrderChoices = (choices: DraggableListChoices[]) =>
  choices.map(choice => choice.id)

export const useContainersStateControl = (
  responseIdentifier: string,
  simpleChoices: JSX.Element[],
  shuffle: string,
  minChoices: number,
  maxChoices: number,
  questionCompleted?: boolean,
  correctAnswers?: string[]
): UseContainersStateControlType => {
  const { currentItemResponse, currentTestPart } = useSnapshot(CurrentTestState)
  const itemResponse = useReactiveItemResponse(responseIdentifier)

  const [initialChoices, setInitialChoices] =
    useState<DraggableListChoices[]>(null)
  const [sortableChoices, setSortableChoices] =
    useState<DraggableListChoices[]>(null)

  const isValid = useResponseValidityUpdate(
    currentItemResponse?.id,
    sortableChoices?.length || 0,
    minChoices,
    maxChoices
  )

  useResponseUpdate(
    currentItemResponse?.id,
    responseIdentifier,
    sortableChoices,
    InteractionType.order,
    transformOrderChoices,
    isValid
  )

  useEffect(() => {
    if (!itemResponse) return

    setInitialChoices(getInitialChoices(itemResponse, simpleChoices, shuffle))
    setSortableChoices(getInitialSortableChoices(itemResponse, simpleChoices))
    // eslint-disable-next-line
  }, [itemResponse, shuffle])

  useEffect(() => {
    if (questionCompleted && !!correctAnswers) {
      // Create a lookup map for fast access
      const allChoices = getAllChoices(simpleChoices)
      const mapOfChoices = new Map(
        allChoices.map(choice => [choice.id, choice])
      )

      // Build the result in correct order
      const sortedAnswers = []
      for (const id of correctAnswers) {
        const item = mapOfChoices.get(id)
        if (item) sortedAnswers.push(item)
      }

      // Save
      setSortableChoices(sortedAnswers)
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [questionCompleted, correctAnswers])

  const moveChoice = useCallback(
    (type: MoveChoiceType) => (choiceId: string) => {
      if (currentTestPart?.isFinished) return

      switch (type) {
        case MoveChoiceEnum.toInit: {
          setInitialChoices(state => [
            ...state,
            sortableChoices.find(choice => choice.id === choiceId)
          ])
          setSortableChoices(state =>
            state.filter(choice => choice.id !== choiceId)
          )
          break
        }
        case MoveChoiceEnum.fromInit: {
          setSortableChoices(state => [
            ...state,
            initialChoices.find(choice => choice.id === choiceId)
          ])
          setInitialChoices(state =>
            state.filter(choice => choice.id !== choiceId)
          )
          break
        }
      }
    },
    [currentTestPart?.isFinished, sortableChoices, initialChoices]
  )

  // Reset initial state when response ID changes.
  // const currentResponseRef = useUpdatingRef(itemResponse)
  // const simpleChoicesRef = useUpdatingRef(simpleChoices)

  // useEffect(() => {
  //   setInitialChoices(getInitialChoices(currentResponseRef.current, simpleChoicesRef.current, shuffle))
  //   setSortableChoices(getInitialSortableChoices(currentResponseRef.current, simpleChoicesRef.current))
  // }, [currentItemResponse?.id, currentResponseRef, simpleChoicesRef, shuffle])

  return [initialChoices, sortableChoices, setSortableChoices, moveChoice]
}

export const useDragDropChoiceControl = (
  initialChoices: DraggableListChoices[],
  sortableChoices: DraggableListChoices[],
  setSortableChoices: Dispatch<SetStateAction<DraggableListChoices[]>>,
  moveChoice: (type: MoveChoiceType) => (choiceId: string) => void
): UseDragDropChoiceControlType => {
  const [activeId, setActiveId] = useState<string | null>()
  const [startContainer, setStartContainer] = useState<string | null>(null)

  // Track drag operations to prevent infinite loops
  const dragCountRef = useRef(0)
  const lastDragResetRef = useRef(Date.now())
  const MAX_DRAGS_PER_SECOND = 30

  // Cache these sets for O(1) lookups instead of O(n) array searches
  const initialChoicesSet = useRef<Set<string>>(new Set())
  const sortableChoicesSet = useRef<Set<string>>(new Set())

  // Update cached sets when choices change
  useEffect(() => {
    initialChoicesSet.current = new Set(getListChoicesIds(initialChoices || []))
  }, [initialChoices])

  useEffect(() => {
    sortableChoicesSet.current = new Set(
      getListChoicesIds(sortableChoices || [])
    )
  }, [sortableChoices])

  const sortingChoices = useCallback(
    (dragChoiceId: string, dropId: string) => {
      setSortableChoices(state => {
        const sortableChoicesIds = getListChoicesIds(state)
        const dragChoiceIndex = getChoiceIndex(sortableChoicesIds, dragChoiceId)
        const dropChoiceIndex = getChoiceIndex(sortableChoicesIds, dropId)

        if (
          dragChoiceIndex !== dropChoiceIndex &&
          dragChoiceIndex !== -1 &&
          dropChoiceIndex !== -1
        ) {
          return arrayMove(state, dragChoiceIndex, dropChoiceIndex)
        }
        return state
      })
    },
    [setSortableChoices]
  )

  const handleDragStart = useCallback((e: DragStartEvent) => {
    setActiveId(`${e.active.id}`)
  }, [])

  const handleDragEnd = useCallback(() => {
    setActiveId(null)
    setStartContainer(null)
  }, [])

  const handleCancelDrag = useCallback(() => {
    // Use cached sets for O(1) lookups
    const isInInitial = initialChoicesSet.current.has(activeId)
    const isInSortable = sortableChoicesSet.current.has(activeId)
    const isStartInSortable = sortableChoicesSet.current.has(startContainer)

    if (
      !(startContainer === ContainersId.INITIAL_DROP && isInInitial) &&
      !(isStartInSortable && isInSortable)
    ) {
      startContainer === ContainersId.INITIAL_DROP
        ? moveChoice("toInit")(activeId)
        : moveChoice("fromInit")(activeId)
    }

    handleDragEnd()
  }, [activeId, startContainer, moveChoice, handleDragEnd])

  const handleDragOver = useCallback(
    (e: DragOverEvent) => {
      const dragChoiceId = `${e.active.id}`
      const dropId = `${e.over?.id}`

      setStartContainer(state => state || `${e.over?.id}`)

      if (!dropId) return

      // Track drag frequency to prevent infinite loops
      const now = Date.now()
      if (now - lastDragResetRef.current > 1000) {
        // Reset counter every second
        dragCountRef.current = 0
        lastDragResetRef.current = now
      } else {
        // Increment drag count when dragging
        dragCountRef.current++
        if (dragCountRef.current > MAX_DRAGS_PER_SECOND) {
          console.log(
            "Excessive dragging detected, returning item to initial container"
          )

          // Return item to initial container if it's in sortable
          if (sortableChoicesSet.current.has(dragChoiceId)) {
            moveChoice(MoveChoiceEnum.toInit)(dragChoiceId)
          }

          // Reset drag state
          setActiveId(null)
          setStartContainer(null)
          dragCountRef.current = 0
          lastDragResetRef.current = now
          return
        }
      }

      // Use cached sets for O(1) lookups
      const isInInitial = initialChoicesSet.current.has(dragChoiceId)
      const isInSortable = sortableChoicesSet.current.has(dragChoiceId)
      const isDropInSortable = sortableChoicesSet.current.has(dropId)

      // Sortable container --> Initial container
      if (dropId === ContainersId.INITIAL_DROP && isInSortable) {
        moveChoice(MoveChoiceEnum.toInit)(dragChoiceId)
        return
      }

      // Initial container --> Sortable container
      if (
        (dropId === ContainersId.SORTABLE_DROP || isDropInSortable) &&
        isInInitial
      ) {
        moveChoice(MoveChoiceEnum.fromInit)(dragChoiceId)
        // Only sort if dropping on a specific item, not the container itself
        if (dropId !== ContainersId.SORTABLE_DROP) {
          sortingChoices(dragChoiceId, dropId)
        }
        return
      }

      // Sorting within sortable container
      if (isInSortable && isDropInSortable && dragChoiceId !== dropId) {
        sortingChoices(dragChoiceId, dropId)
      }
    },
    [moveChoice, sortingChoices]
  )

  const activationConstraint = {
    distance: 15
  }

  const sensors = useSensors(
    useSensor(MouseSensor, {
      activationConstraint
    }),
    useSensor(KeyboardSensor)
  )

  // Custom collision detection algorithm
  const initialDragContainer = useRef<Collision[]>([])
  const started = useRef(false)
  const currentDragEvent = useRef<DragEvents | null>(null)

  useEffect(() => {
    if (!activeId) {
      started.current = false
      initialDragContainer.current = []
    }
  }, [activeId])

  const handleDragMove = useCallback((e: DragMoveEvent) => {
    currentDragEvent.current = e.activatorEvent.type as DragEvents
  }, [])

  const customCollisionDetectionAlgorithm: CollisionDetection = useCallback(
    args => {
      // Early return if no active drag
      if (!activeId) return []

      const pointerCollisions =
        currentDragEvent.current === DragEvents.MOUSE_DOWN
          ? pointerWithin(args)
          : rectIntersection(args)

      if (!started.current) {
        initialDragContainer.current = pointerCollisions.filter(
          ({ id }) => id !== activeId
        )
        started.current = true
      }

      if (pointerCollisions.length > 0) {
        return pointerCollisions
      }

      return initialDragContainer.current
    },
    [activeId]
  )
  // Custom collision detection algorithm

  return [
    activeId,
    handleDragStart,
    handleDragEnd,
    handleDragOver,
    sortingChoices,
    sensors,
    customCollisionDetectionAlgorithm,
    handleDragMove,
    handleCancelDrag
  ]
}

export const useSaveStudentAnswer = (
  manualActiveId: string,
  draggableActiveId: string,
  sortableChoices: DraggableListChoices[],
  minChoices: number,
  maxChoices: number,
  onSaveStudentAnswer?: onSaveStudentAnswer,
  questionComplete?: boolean,
  removingChoiceId?: string | null
) => {
  const debouncedSortableChoices = useDebouncing(sortableChoices, 500)

  const [choicesIds, setChoicesIds] = useState<string[]>(null)
  const [lastSelectedChoiceId, setLastSelectedChoiceId] = useState<string>(null)
  const [answerRevealedSelectedChoices, setAnswerRevealedSelectedChoices] =
    useState<Map<string, boolean>>(null)
  const [dragCompletedChoiceId, setDragCompletedChoiceId] =
    useState<string>(null)
  const previousSortableChoicesRef = useRef<DraggableListChoices[]>(null)

  useEffect(() => {
    if (!!draggableActiveId || !debouncedSortableChoices) return

    if (
      choicesIds?.length !== debouncedSortableChoices?.length ||
      !choicesIds?.every((id, i) => debouncedSortableChoices?.[i].id === id)
    ) {
      setChoicesIds(debouncedSortableChoices.map(item => item.id))
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [draggableActiveId, debouncedSortableChoices])

  useEffect(() => {
    if (!manualActiveId && !draggableActiveId) return

    setLastSelectedChoiceId(manualActiveId || draggableActiveId)
  }, [manualActiveId, draggableActiveId])

  // Track when drag completes and item is placed in the sortable container
  useEffect(() => {
    if (draggableActiveId) {
      // Drag is active, clear completed state
      setDragCompletedChoiceId(null)
    } else if (
      lastSelectedChoiceId &&
      sortableChoices?.some(choice => choice?.id === lastSelectedChoiceId)
    ) {
      // Drag ended and the item is in sortable choices
      setDragCompletedChoiceId(lastSelectedChoiceId)
    }

    // Clear dragCompletedChoiceId if the item is removed
    if (
      dragCompletedChoiceId &&
      !sortableChoices?.some(choice => choice?.id === dragCompletedChoiceId)
    ) {
      setDragCompletedChoiceId(null)
    }
  }, [
    draggableActiveId,
    lastSelectedChoiceId,
    sortableChoices,
    dragCompletedChoiceId
  ])

  useEffect(
    () => {
      // Use sortableChoices directly instead of debounced choicesIds
      if (!sortableChoices) return

      // Check if an item was removed (length decreased)
      const wasItemRemoved =
        previousSortableChoicesRef.current &&
        previousSortableChoicesRef.current.length > sortableChoices.length

      // Skip calling onSaveStudentAnswer when removing items
      if (wasItemRemoved || removingChoiceId) {
        // Update the ref but don't trigger feedback
        previousSortableChoicesRef.current = sortableChoices
        return
      }

      // Only proceed if not actively dragging
      if (draggableActiveId) return

      // Prevent feedback during drag operations
      const currentSelectedChoiceId = questionComplete
        ? null
        : manualActiveId || dragCompletedChoiceId || null

      const currentChoiceIds = sortableChoices.map(item => item.id)

      const answersRevealed = onSaveStudentAnswer?.({
        interactionType: InteractionType.order,
        answerId: currentSelectedChoiceId,
        minChoices,
        maxChoices,
        allAnswersIds: currentChoiceIds || [],
        revealAnswersWithoutSaving: !currentSelectedChoiceId // repopulate answer state if student is continueing where they left off
      })
      if (answersRevealed && !!answersRevealed.answerStateValue) {
        const answerMap = new Map(
          answersRevealed.answerStateValue.map(a => [a.answerId, a.isCorrect])
        )
        setAnswerRevealedSelectedChoices(answerMap)
      } else {
        setAnswerRevealedSelectedChoices(null)
      }

      // Update the ref for next comparison
      previousSortableChoicesRef.current = sortableChoices
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [
      sortableChoices,
      minChoices,
      maxChoices,
      manualActiveId,
      dragCompletedChoiceId,
      removingChoiceId,
      draggableActiveId
    ]
  )

  return { answerRevealedSelectedChoices }
}
